import { Box, useTheme } from "@mui/material";
import Typography from "components/basic/typography";

interface QuoteHeaderProps {
  logoUrl: string | null;
  businessName: string;
  address: string;
  email: string;
  phoneNumber: string;
  website: string | null;
}

/**
 * Top of the estimate document: vendor brand (logo or name) on the left and the
 * vendor's business contact block on the right.
 */
const QuoteHeader: React.FC<QuoteHeaderProps> = ({
  logoUrl,
  businessName,
  address,
  email,
  phoneNumber,
  website,
}) => {
  const theme = useTheme();

  const containerSx = {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: "flex-start",
    gap: theme.spacing(2),
  };

  const brandSx = {
    flexGrow: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  };

  const logoStyle: React.CSSProperties = {
    maxWidth: "100%",
    width: "240px",
    maxHeight: "80px",
    objectFit: "contain",
  };

  const businessNameStyle: React.CSSProperties = {
    fontWeight: "bold",
    lineHeight: 1.05,
  };

  // Right-aligning the contact block only reads correctly beside the brand;
  // once stacked it has to fall back to the reading edge.
  const contactBlockSx = {
    textAlign: { xs: "left", sm: "right" },
  };

  return (
    <Box sx={containerSx}>
      <Box sx={brandSx}>
        {logoUrl ? (
          <img src={logoUrl} alt={businessName} style={logoStyle} />
        ) : (
          <Typography
            variant="h2"
            style={businessNameStyle}
            sx={{ fontSize: "clamp(1.6rem, 5vw, 2.6em)" }}
          >
            {businessName}
          </Typography>
        )}
      </Box>

      <Box sx={contactBlockSx}>
        <Typography variant="body2">{businessName}</Typography>
        <Typography variant="body2">{address}</Typography>
        {phoneNumber ? (
          <Typography variant="body2">{phoneNumber}</Typography>
        ) : null}
        {email ? <Typography variant="body2">{email}</Typography> : null}
        {website ? <Typography variant="body2">{website}</Typography> : null}
      </Box>
    </Box>
  );
};

export default QuoteHeader;
